
import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgSelectComponent } from '@ng-select/ng-select'
import { NgbModal, NgbNavModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { UserService } from '@services/core/user.service';
import { PanelComponent } from '@components/core-template/panel/panel.component';

@Component({
  selector: 'app-user-roles',
  templateUrl: './role.component.html',
  styleUrls: ["./role.component.css"],
  imports: [PanelComponent, FormsModule, ReactiveFormsModule, NgbTooltipModule],
  providers: [UserService],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserRolesComponent implements OnInit {

  private _userService = inject(UserService)
  private _fb = inject(FormBuilder)
  private _modalService = inject(NgbModal)
  private destroyRef = inject(DestroyRef)
  private cdr = inject(ChangeDetectorRef)
  protected roles: any
  protected search = ""

  private modalOptions = {
    size: 'xl',
    scrollable: true,
    animation: true,
    backdrop: true
  }
  public closeResult = ""
  protected newRoleForm = this._fb.group({
    _id: [null],
    name: ["", Validators.required],
    description: ["", Validators.required],
    priority: [0, Validators.required],
    enabled: [true],
  })

  ngOnInit(): void {
    this.getRoles();
  }

  open(modal: any, role?: any) {
    this.newRoleForm.reset();
    this.newRoleForm.patchValue({ enabled: true })
    if (role) this.newRoleForm.patchValue(role)
    this._modalService.open(modal, this.modalOptions).result.then((result) => {
      this.closeResult = `Closed with: ${result}`;
    }, (reason) => {
      this.closeResult = `${reason}`;
    });
  }

  getRoles() {
    this._userService.getRoles<any[]>().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((roles: any[]) => {
      this.roles = roles.map((r, i) => ({ ...r, priority: i }));
      this.cdr.markForCheck()
    });
  }

  saveRole() {
    const formValue = this.newRoleForm.value;
    if (formValue._id) {
      this._userService.updateRole(formValue).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.getRoles();
      });
    } else {
      formValue.priority = this.roles.length;
      this._userService.createRole(formValue).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.getRoles();
      });
    }
  }

  moveUp(role: any): void {
    const index = this.roles.indexOf(role);
    if (index <= 0) return;
    this.swapPriority(index, index - 1);
  }

  moveDown(role: any): void {
    const index = this.roles.indexOf(role);
    if (index >= this.roles.length - 1) return;
    this.swapPriority(index, index + 1);
  }

  private swapPriority(i1: number, i2: number): void {
    const prevPriority1 = this.roles[i1].priority;
    const prevPriority2 = this.roles[i2].priority;
    this.roles[i1].priority = prevPriority2;
    this.roles[i2].priority = prevPriority1;

    const id1 = this.roles[i1]._id;
    const id2 = this.roles[i2]._id;
    const copy = [...this.roles].sort((a, b) => a.priority - b.priority);

    const role1 = copy.find(r => r._id === id1);
    const role2 = copy.find(r => r._id === id2);
    this.roles = copy;

    if (role1) this._userService.updateRole({ _id: role1._id, priority: role1.priority }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    if (role2) this._userService.updateRole({ _id: role2._id, priority: role2.priority }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  enabledRole(role: any) {
    role.enabled = !role.enabled
    this._userService.updateRole(role).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.getRoles();
    });
  }

} 
